import type { Experiment, ExperimentPipeline, PipelineStage } from 'types';
import type { BoardPayloadStage, PipelineBoardResponse, StageCollisions, StageDraft } from './types';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { useWebsites } from '@/state/useWebsites';
import { toast } from '@/hooks/use-toast';
import { isPreStart } from 'helpers/experiment-status';
import { apiErrorMessage } from '../api-error';
import { isFrozen } from './types';

/**
 * One board, edited locally and saved whole.
 *
 * The draft lives here rather than in the columns because a drag is not a change to one
 * column — it removes a card from one and adds it to another, and the collision warnings
 * for both have to be recomputed together. A component holding its own list would have to
 * tell its sibling what happened.
 *
 * Collisions come from the server on every change rather than being computed here. The
 * model reads each variant's changes and each URL targeting row, which this page never
 * loads, and duplicating the rules in the client is how the editor and the engine end up
 * disagreeing about the same board.
 */

const COLLISION_DEBOUNCE_MS = 350;

/**
 * A stand-in for a stage the debounced collision check has not evaluated yet.
 * 'safe' rather than a distinct verdict, since nothing should render as blocked
 * before the server has actually said so — the real result overwrites this within
 * COLLISION_DEBOUNCE_MS of the stage being added.
 */
const emptyCollision = (stageIndex: number): StageCollisions => ({
  stageId: null,
  stageIndex,
  worst: 'safe',
  pairs: [],
});

/**
 * Whether any known collision blocks the board. Exported and pure so the guard is
 * covered directly: `collisions` is meant to stay index-aligned with `stages`, but
 * a debounced re-check can land mid-edit, so a missing/`undefined` entry has to
 * read as non-blocking rather than throw reading `.worst` off it.
 */
export const hasBlockingCollision = (collisions: readonly (StageCollisions | undefined)[]): boolean =>
  collisions.some((stage) => stage?.worst === 'block');

export const usePipelineBoard = (pipelineId: number | null) => {
  const apiClient = useClient();
  const queryClient = useQueryClient();
  const { website } = useWebsites();
  const websiteId = website?.id;

  const { data, isLoading: isLoadingBoard } = useQuery<PipelineBoardResponse>({
    enabled: !!pipelineId,
    queryKey: ['PIPELINE_BOARD', pipelineId],
    queryFn: () => apiClient.get<PipelineBoardResponse>(`/pipelines/${pipelineId}`),
  });

  const { data: experiments, isLoading: isLoadingExperiments } = useQuery<Experiment[]>({
    enabled: !!websiteId,
    queryKey: ['EXPERIMENTS', websiteId],
    queryFn: () => apiClient.get<Experiment[]>(`/websites/${websiteId}/experiments`),
  });

  // Both queries feed the board: a card's id comes from PIPELINE_BOARD, but its name
  // and status come from experimentsById, built from EXPERIMENTS below. Reporting only
  // the board's own isLoading let the board render before EXPERIMENTS resolved, which
  // left experimentsById empty and made stage.experimentIds.flatMap(...) silently drop
  // every id it could not resolve yet — a populated pipeline rendered as if every
  // stage were empty until the second query caught up.
  const isLoading = isLoadingBoard || isLoadingExperiments;

  const [stages, setStages] = useState<StageDraft[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [collisions, setCollisions] = useState<StageCollisions[]>([]);

  const pipeline = data?.pipeline ?? null;

  /**
   * Seeded from the server, keyed on what the server last wrote.
   *
   * The key matters: initialising state from a query that has not resolved leaves an
   * empty board in the editor, and the next save would post it — the same trap the
   * schedule form hit, where an unloaded form saved its own blank fields back.
   */
  const serverVersion = pipeline ? `${pipeline.id}:${pipeline.updatedAt ?? 0}` : null;

  /**
   * Read through a ref rather than listed as a dependency.
   *
   * Unsaved edits must survive a refetch. Pausing a pipeline invalidates this query, which
   * bumps updatedAt, which would re-seed the board and throw away whatever was half
   * arranged — the status buttons do not touch stages, so re-seeding on their account is
   * pure loss. Depending on isDirty instead would re-run the effect on the first drag and
   * wipe it immediately, which is the bug it is meant to prevent.
   */
  const isDirtyRef = useRef(false);
  isDirtyRef.current = isDirty;

  useEffect(() => {
    if (!pipeline) return;
    if (isDirtyRef.current) return;
    setStages(toDrafts(pipeline));
    setCollisions(data?.collisions ?? []);
    setIsDirty(false);
  }, [serverVersion]);

  // Re-checked against the server whenever the arrangement changes, debounced so a
  // drag across three columns is one request rather than three.
  useEffect(() => {
    if (!websiteId || !isDirty) return;

    const timer = setTimeout(async () => {
      try {
        const result = await apiClient.post<{ collisions: StageCollisions[] }>('/pipelines/collisions', {
          websiteId,
          stages: stages.map((stage) => ({ id: stage.id, experimentIds: stage.experimentIds })),
        });
        setCollisions(result.collisions);
      } catch {
        // A failed check must not block editing. The save is authoritative and runs the
        // same rules, so the worst case is warnings that lag by one change.
      }
    }, COLLISION_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [stages, isDirty, websiteId]);

  const edit = (next: StageDraft[]) => {
    setStages(next);
    setIsDirty(true);
  };

  const placedIds = new Set(stages.flatMap((stage) => stage.experimentIds));

  /**
   * The tray: drafts not already on this board.
   *
   * Only drafts, because a pipeline exists to start what is on it and the API refuses
   * anything else. An experiment already claimed by a different pipeline is not filtered
   * out here — this page cannot see other boards, and the save answers with a message
   * naming the pipeline that holds it, which is more use than a card that silently
   * never appears.
   */
  const available = (experiments ?? []).filter(
    (experiment) =>
      isPreStart(experiment.status) && !experiment.archived && !experiment.deleted && !placedIds.has(experiment.id),
  );

  const moveToStage = (experimentId: number, targetIndex: number) => {
    const target = stages[targetIndex];
    if (!target || isFrozen(target)) return;
    if (target.experimentIds.includes(experimentId)) return;

    edit(
      stages.map((stage, index) => {
        if (index === targetIndex) return { ...stage, experimentIds: [...stage.experimentIds, experimentId] };
        // Removed from wherever it was, unless that stage has already run — a frozen
        // stage's membership is what actually happened and cannot be edited.
        if (isFrozen(stage)) return stage;
        return { ...stage, experimentIds: stage.experimentIds.filter((id) => id !== experimentId) };
      }),
    );
  };

  const removeFromBoard = (experimentId: number) => {
    edit(
      stages.map((stage) =>
        isFrozen(stage) ? stage : { ...stage, experimentIds: stage.experimentIds.filter((id) => id !== experimentId) },
      ),
    );
  };

  const addStage = () => {
    // collisions is read positionally by collisionFor and hasBlocking, matched
    // against stages by index — the same reasoning deleteStage's filter and
    // moveStage's swap already follow. Without this, collisions stays one shorter
    // than stages until the debounced re-check lands (or indefinitely, since a
    // failed re-check is swallowed), and a later moveStage swapping into the
    // missing slot writes an explicit `undefined` into it.
    setCollisions((prev) => [...prev, emptyCollision(stages.length)]);
    edit([
      ...stages,
      {
        id: null,
        name: `Stage ${stages.length + 1}`,
        advanceRule: 'all-concluded',
        state: 'pending',
        experimentIds: [],
      },
    ]);
  };

  const renameStage = (index: number, name: string) => {
    edit(stages.map((stage, at) => (at === index ? { ...stage, name } : stage)));
  };

  const setAdvanceRule = (index: number, advanceRule: PipelineStage['advanceRule']) => {
    edit(stages.map((stage, at) => (at === index ? { ...stage, advanceRule } : stage)));
  };

  const deleteStage = (index: number) => {
    const stage = stages[index];
    if (!stage || isFrozen(stage)) return;
    edit(stages.filter((_, at) => at !== index));
    // collisions is read positionally by collisionFor, matched against stages by index
    // rather than by stage id (a new, unsaved stage has no id to match on yet). Removing
    // a slot here too is what keeps every later column's collision report — and
    // hasBlocking, which the same array backs — pointing at the stage actually there,
    // rather than the one that used to be one slot further along, until the debounced
    // re-check lands up to 350ms later.
    setCollisions((prev) => prev.filter((_, at) => at !== index));
  };

  /**
   * Stage order is changed with header buttons rather than by dragging columns.
   * Dragging a column and dragging a card into a column are the same gesture over the
   * same target, and getting the wrong one is a rearrangement nobody asked for.
   */
  const moveStage = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= stages.length) return;
    if (isFrozen(stages[index]!) || isFrozen(stages[target]!)) return;

    const next = [...stages];
    [next[index], next[target]] = [next[target]!, next[index]!];
    edit(next);

    // Same reasoning as deleteStage: swap the two collision slots along with the two
    // stages, so collisionFor(index) keeps reporting on the stage now at that index
    // instead of the one that was there before the swap.
    setCollisions((prev) => {
      const swapped = [...prev];
      [swapped[index], swapped[target]] = [swapped[target]!, swapped[index]!];
      return swapped;
    });
  };

  const saveBoard = async (): Promise<boolean> => {
    if (!pipelineId) return false;
    setIsSaving(true);

    const payload: { stages: BoardPayloadStage[] } = {
      stages: stages.map((stage) => ({
        id: stage.id,
        name: stage.name.trim() || 'Untitled stage',
        advanceRule: stage.advanceRule,
        experimentIds: stage.experimentIds,
      })),
    };

    try {
      const result = await apiClient.put<PipelineBoardResponse>(`/pipelines/${pipelineId}/board`, payload);
      setStages(toDrafts(result.pipeline));
      setCollisions(result.collisions);
      setIsDirty(false);
      queryClient.setQueryData(['PIPELINE_BOARD', pipelineId], result);
      queryClient.invalidateQueries({ queryKey: ['PIPELINES', websiteId] });
      return true;
    } catch (error) {
      // Verbatim again: every rejection names the stage and says what about it cannot
      // change, which is the whole message.
      toast({
        title: 'Could not save the board',
        description: apiErrorMessage(error),
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const saveSettings = async (settings: {
    name: string;
    description: string;
    automationEnabled: boolean;
    autoDeployEnabled: boolean;
  }): Promise<boolean> => {
    if (!pipelineId) return false;
    try {
      await apiClient.put(`/pipelines/${pipelineId}`, settings);
      queryClient.invalidateQueries({ queryKey: ['PIPELINE_BOARD', pipelineId] });
      queryClient.invalidateQueries({ queryKey: ['PIPELINES', websiteId] });
      return true;
    } catch (error) {
      toast({
        title: 'Could not update the pipeline',
        description: apiErrorMessage(error),
      });
      return false;
    }
  };

  const setStatus = async (status: ExperimentPipeline['status']): Promise<boolean> => {
    if (!pipelineId) return false;
    try {
      await apiClient.post(`/pipelines/${pipelineId}/status`, { status });
      queryClient.invalidateQueries({ queryKey: ['PIPELINE_BOARD', pipelineId] });
      queryClient.invalidateQueries({ queryKey: ['PIPELINES', websiteId] });
      return true;
    } catch (error) {
      toast({
        title: 'Could not change the status',
        description: apiErrorMessage(error),
      });
      return false;
    }
  };

  /** Ends the running stage now. The engine arms the next one on its following tick. */
  const advanceStage = async (): Promise<boolean> => {
    if (!pipelineId) return false;
    try {
      const result = await apiClient.post<PipelineBoardResponse>(`/pipelines/${pipelineId}/advance`, {});
      setStages(toDrafts(result.pipeline));
      setCollisions(result.collisions);
      setIsDirty(false);
      queryClient.setQueryData(['PIPELINE_BOARD', pipelineId], result);
      return true;
    } catch (error) {
      toast({
        title: 'Could not advance the pipeline',
        description: apiErrorMessage(error),
      });
      return false;
    }
  };

  const runningStageIndex = stages.findIndex((stage) => stage.state === 'running');

  const collisionFor = (index: number): StageCollisions | null => collisions[index] ?? null;
  const hasBlocking = hasBlockingCollision(collisions);

  return {
    pipeline,
    isLoading,
    stages,
    collisions,
    collisionFor,
    hasBlocking,
    available,
    experimentsById: new Map((experiments ?? []).map((experiment) => [experiment.id, experiment])),
    isDirty,
    isSaving,
    runningStageIndex,
    setStatus,
    advanceStage,
    moveToStage,
    removeFromBoard,
    addStage,
    renameStage,
    setAdvanceRule,
    deleteStage,
    moveStage,
    saveBoard,
    saveSettings,
  };
};

const toDrafts = (pipeline: ExperimentPipeline): StageDraft[] =>
  (pipeline.stages ?? []).map((stage) => ({
    id: stage.id,
    name: stage.name,
    advanceRule: stage.advanceRule,
    state: stage.state,
    experimentIds: (stage.items ?? []).map((item) => item.experimentId),
  }));
